// redux used for any region related
// region included telCode
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { MODULE_TYPE, OTP_VERIFICATION_METHOD } from '@utils/constants';

import { setSiteKey } from './turnstile-slice';
import { setEnableDynamicForm } from './register-slice';

const initialState = {
    enableNewOTP: true,
    status: false,
    fetchStatus: 'idle',
    moduleFetchStatus: {
        // e.g. module from constant.MODULE_TYPE
    },
    // raw data from JSON
    rawData: {
        cryptoAddress: null,
        withdrawalCryptoAddress: null,
    },
    processedData: {
        cryptoAddress: null,
        withdrawalCryptoAddress: null,
    },
    moduleData: {},
    fetchedModuleTypes: [],
};

// Define a common async function handler
// const asyncThunkHandler = async (fn) => {
//     try {
//         return await fn();
//     } catch (err) {
//         console.error(err);
//     }
// };

/**
 * @param {String} authType - constant value from OTP_VERIFICATION_TYPE, e.g. CRYPTO_ADDRESS
 * @param {Object} splConfig - isPublic, authSettingType, version
 */
export const fetchAuthSettings = createAsyncThunk('auth-settings-slice/fetchAuthSettings', async ({ splConfig }, { dispatch, getState, rejectWithValue }) => {
    const { portal, authSettingsReducer, user, turnstileReducer } = getState();

    if (!splConfig) {
        return rejectWithValue('No splConfig provided'); // Use rejectWithValue
    }

    if (!splConfig?.isPublic) {
        splConfig = {
            ...splConfig,
            isPublic: false,
        };
    }

    splConfig = {
        ...splConfig,
        isAffiliate: window.isAffiliate,
    };

    const authType = splConfig?.authSettingsType;

    // v1 data caching
    if (authSettingsReducer?.rawData?.[authType] && !splConfig?.isReqUpdate) {
        // return { [authType]: authSettingsReducer.rawData[authType] };

        return {
            authType,
            rawData: authSettingsReducer.rawData[authType],
            processedData: authSettingsReducer.processedData[authType],
        };
    }
    // v2 data caching
    if (splConfig?.version === 'v2' && authSettingsReducer?.moduleData?.[authType] && !splConfig?.isReqUpdate) {
        return {
            authType,
            moduleData: authSettingsReducer.moduleData[authType],
        };
    }

    let getCategorizedAuthSettings = window.isAffiliate ? window.SPL_Affiliate.getCategorizedAuthSettings : window.SPL_Member.getCategorizedAuthSettings;

    if (splConfig?.version === 'v2') {
        getCategorizedAuthSettings = window.SPL_Member.getCategorizedAuthSettingsV2;
    }

    if (!getCategorizedAuthSettings) {
        dispatch(updatefetchStatus('failed'));
        dispatch(setModuleFetchStatus({ module: authType, status: 'failed' }));
        return;
    }

    try {
        const response = await getCategorizedAuthSettings(splConfig);
        if (response) {
            dispatch(updateFetchedModuleTypes(authType));
        }
        if (splConfig?.version === 'v2') {
            const disableTurnstile = (response) => {
                response.otpTurnstileEnabled = false;
                response.turnstileEnabled = false;
            };
            const skipTurnstileForLiteApp = window.SPL_LiteApp.isLiteApp() && !portal?.settings?.isAllowTurnstileForLiteApp;
            // if skip site key early exist the process
            if (!skipTurnstileForLiteApp) {
                try {
                    if (!turnstileReducer?.site_key) {
                        const siteKeyResponse = await window.SPL_Member.getCloudflareTurnstileSetting();
                        // const passkey = '1x00000000000000000000AA';
                        // const blockkey = '2x00000000000000000000AB';
                        // const forcekey = '3x00000000000000000000FF';
                        const siteKey = siteKeyResponse?.site_key;
                        // Set turnstile flags only if siteKey exists
                        if (siteKey) {
                            dispatch(setSiteKey(siteKey));
                        } else {
                            disableTurnstile(response);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching Cloudflare Turnstile setting:', error);
                    disableTurnstile(response);
                }
            } else {
                disableTurnstile(response);
            }
            if (authType === MODULE_TYPE.REGISTRATION) {
                dispatch(setEnableDynamicForm(response?.enable_dynamic_form?.enable || portal?.settings?.registerConfiguration?.enable_dynamic_form || false));
            }
            return { authType, processedData: response, version: 'v2' };
        } else {
            let processedAuthMethods = null;
            if (response?.data?.[OTP_VERIFICATION_METHOD.OTP]?.authMethod) {
                processedAuthMethods = (response?.data?.[OTP_VERIFICATION_METHOD.OTP]?.authMethod || []).filter((method) => {
                    if ((method === 'PHONE' && !user?.account?.phone) || (method === 'EMAIL' && !user?.account?.email)) {
                        return false;
                    }
                    return true;
                });
                // handle userverification module enable or not
                if (authType === MODULE_TYPE.USER_VERIFICATION) {
                    response.data.userVerificationEmailEnabled = response?.data?.[OTP_VERIFICATION_METHOD.OTP]?.authMethod.includes(OTP_VERIFICATION_METHOD.EMAIL);
                    response.data.userVerificationPhoneEnabled =
                        response?.data?.[OTP_VERIFICATION_METHOD.OTP]?.authMethod.includes(OTP_VERIFICATION_METHOD.PHONE) ||
                        response?.data?.[OTP_VERIFICATION_METHOD.OTP]?.authMethod.includes(OTP_VERIFICATION_METHOD.WHATSAPP);
                }
            } else {
                processedAuthMethods = (response?.data?.authMethod || []).filter((method) => {
                    if (method.type === 'PHONE' && !user?.account?.phone) {
                        return false;
                    }
                    if (method.type === 'EMAIL' && !user?.account?.email) {
                        return false;
                    }
                    return true;
                });
            }
            const processedData = {
                ...response?.data,
                authMethod: processedAuthMethods,
            };

            // return { [authType]: response?.data };
            return { authType, rawData: response?.data, processedData: processedData };
        }
    } catch (error) {
        dispatch(updatefetchStatus('failed'));
        dispatch(setModuleFetchStatus({ module: authType, status: 'failed' }));
        console.error('Error fetching auth settings:', error);
        return rejectWithValue(error.message || 'An error occurred'); // Provide a default error message
    }
});

export const authSettingsSlice = createSlice({
    name: 'auth-settings',
    initialState,
    reducers: {
        updateAuthSettings: (state, action) => {
            const { infoType, infoTypeKey, infoTypeValue, moduleType } = action.payload;
            const authSettings = [
                'AFF_SIX_PIN',
                'AFF_BANK_DETAIL_BANK',
                'AFF_BANK_DETAIL_CRYPTO',
                'AFF_BANK_DETAIL_EWALLET',
                'AFF_WITHDRAWAL_BANK',
                'AFF_WITHDRAWAL_CRYPTO',
                'AFF_WITHDRAWAL_EWALLET',
                'AFF_WITHDRAWAL_WIRE_TRANSFER',
            ];

            const updateState = (module) => {
                if (infoTypeKey) {
                    if (state.rawData?.[module]?.[infoType]) {
                        state.rawData[module][infoType][infoTypeKey] = infoTypeValue;
                    }
                } else {
                    if (state.rawData?.[module]) {
                        state.rawData[module][infoType] = infoTypeValue;
                    }
                }
            };

            if (moduleType === 'ALL') {
                authSettings.forEach(updateState);
            } else {
                updateState(moduleType);
            }
        },
        updatefetchStatus: (state, action) => {
            state.fetchStatus = action.payload;
        },
        setModuleFetchStatus: (state, action) => {
            const { module, status } = action.payload;
            state.moduleFetchStatus[module] = status;
        },
        updateModuleData: (state, action) => {
            state.moduleData[action.payload.module] = action.payload.data;
        },
        updateTurnstileEnabled: (state, action) => {
            const { module, moduleEnableTurnstile } = action.payload;
            state.moduleData = { ...state.moduleData, [module]: { ...state.moduleData?.[module], turnstileEnabled: moduleEnableTurnstile } };
        },
        updateFetchedModuleTypes: (state, action) => {
            state.fetchedModuleTypes = [...state.fetchedModuleTypes, action.payload];
        },
        updateSixPinRegistered: (state, action) => {
            const { module, sixPinRegistered } = action.payload;
            state.rawData = {
                ...state.rawData,
                [module]: {
                    ...state.rawData[module],
                    [MODULE_TYPE?.SIX_PIN]: {
                        ...state.rawData[module]?.[module],
                        registered: sixPinRegistered,
                    },
                },
            };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchAuthSettings.fulfilled, (state, action) => {
            const { authType, rawData, processedData, version } = action?.payload || {};
            if (version === 'v2') {
                state.moduleData[authType] = processedData;
            } else {
                if (rawData) {
                    state.rawData[authType] = rawData;
                }
                if (processedData) {
                    state.processedData[authType] = processedData;
                }
            }
            state.status = true;
            state.fetchStatus = 'success';
            state.moduleFetchStatus[authType] = 'success';
        });
        builder.addCase(fetchAuthSettings.pending, (state, action) => {
            const {
                meta: {
                    arg: {
                        splConfig: { authSettingsType },
                    },
                },
            } = action;
            state.status = false;
            state.fetchStatus = 'loading';
            state.moduleFetchStatus[authSettingsType] = 'loading';
        });
        builder.addCase(fetchAuthSettings.rejected, (state, action) => {
            const {
                meta: {
                    arg: {
                        splConfig: { authSettingsType },
                    },
                },
            } = action;
            state.fetchStatus = 'failed';
            state.moduleFetchStatus[authSettingsType] = 'loading';
        });
    },
});

export const { updateAuthSettings, updatefetchStatus, setModuleFetchStatus, updateModuleData, updateTurnstileEnabled, updateFetchedModuleTypes, updateSixPinRegistered } =
    authSettingsSlice.actions;

export default authSettingsSlice.reducer;
